// Retry-Regeln des API-Clients als reine Funktionen (ohne Import aus api.ts,
// damit api.ts sie einbinden kann, ohne einen Zyklus zu bauen). Die Fehler
// kommen als `ApiError` (status + name) an; hier reicht die Form.

export interface StatusError {
  status?: number;
  name?: string;
  body?: unknown;
  /** `Retry-After` der Antwort (ApiError.retryAfter). */
  retryAfter?: string | null;
}

/**
 * Darf eine fehlgeschlagene grosse Datei (/upload-file) erneut gesendet
 * werden? Ja bei Netzwerkfehler (status 0), Server-Fehlern (5xx) und 408
 * Request Timeout: Node antwortet mit 408, wenn der Body laenger als sein
 * requestTimeout braucht (Audit 2026-09-24, H5) -- ein langsamer Uplink, kein
 * Grund, den ganzen Upload zu verwerfen. Nie bei Nutzer-Abbruch (AbortError)
 * und nie bei anderen 4xx (die kommen beim Wiederholen identisch zurueck).
 */
export function isRetriableUploadError(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  const err = e as Error & StatusError;
  if (err.name === 'AbortError') return false;
  if (typeof err.status !== 'number') return false;
  return err.status === 0 || err.status === 408 || err.status >= 500;
}

// ── Generierung (TTS/STS) — Audit 2026-09-24, M13 ──────────────────────────

/**
 * Grenzen der Wiederholung — als Objekt, damit Tests sie setzen können
 * (Muster `putRetry` im Dienst). `backoffMs` je Wiederholung, wenn der
 * Server kein Retry-After nennt; `maxDelayMs` kappt auch Retry-After — länger
 * lassen wir das Talent nicht warten.
 */
export const generationRetry = {
  retries: 3,
  backoffMs: [2_000, 5_000, 10_000],
  maxDelayMs: 20_000,
};
/** Client-Fristen je Versuch (der Dienst selbst wartet 90 s / 180 s auf ElevenLabs). */
export const generationTimeouts = {
  ttsMs: 120_000,
  stsMs: 200_000,
};

/**
 * Wiederholen bei 429 (ElevenLabs-Parallelität / Warteschlange des Dienstes,
 * kommt mit Retry-After) — außer den eigenen Grenzen mit `code` — und bei
 * 502/503/504 vom Proxy (Dienst kurz nicht erreichbar, z. B. der Restart
 * beim Deploy, `isProxyOutage`). Nicht bei den bewussten 502/504 des
 * Dienstes (echter ElevenLabs-Fehler bzw. Frist, kommen identisch zurück),
 * nicht bei Nutzer-Abbruch, nicht beim Client-Timeout (der Request lief 2
 * Minuten — noch drei Runden wären 8 Minuten Spinner).
 */
export function isRetriableGenerationError(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  const err = e as Error & StatusError;
  if (err.name === 'AbortError' || err.name === 'TimeoutError') return false;
  if (err.status === 429) return !isFinal429(err);
  return isProxyOutage(err);
}

/**
 * 502/503/504, die nicht vom Dienst selbst stammen, sondern von Caddy, weil
 * der Dienst gerade nicht erreichbar ist — typisch der Deploy-Restart
 * (Graceful Shutdown bis 80 s) oder ein Failover (Integrations-Review
 * 2026-09-24). Caddy antwortet dann mit seiner HTML-Wartungsseite und
 * `Retry-After: 30` (00_Hetzner/config/tro-apps/caddy/Caddyfile, `wartung`).
 * Erkennungsmerkmal: der Body ist NICHT unser JSON `{ success: false, … }`,
 * oder die Antwort nennt ein Retry-After. Die bewussten 502/504 des Dienstes
 * (voiceServiceError, Frist) sind JSON ohne Retry-After und bleiben
 * nicht-retriable — sie kämen identisch zurück.
 */
export function isProxyOutage(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  const err = e as Error & StatusError;
  if (err.name === 'AbortError' || err.name === 'TimeoutError') return false;
  if (err.status !== 502 && err.status !== 503 && err.status !== 504) return false;
  if (err.retryAfter) return true;
  return !isServiceJson(err.body);
}

function isServiceJson(body: unknown): boolean {
  return !!body && typeof body === 'object' && (body as { success?: unknown }).success === false;
}

/**
 * Maschinenlesbarer `code` einer Fehlerantwort unseres Dienstes
 * (`{ success: false, code, message }`), sonst null.
 */
export function errorCode(e: unknown): string | null {
  const body = (e as StatusError | null)?.body;
  if (!body || typeof body !== 'object') return null;
  const code = (body as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

/**
 * 429, die NICHT „später nochmal" heißen (Integrations-Review 2026-09-24):
 * das eigene Kontingent (`quota`, Fenster läuft 10 min) und die
 * Passwort-Sperre (`pw_locked`, 15 min). Jede Wiederholung liefe erneut
 * hinein — beim Kontingent zählte sie früher sogar mit.
 */
function isFinal429(e: StatusError): boolean {
  const code = errorCode(e);
  return code === 'quota' || code === 'pw_locked';
}

/**
 * Pause vor Wiederholung `attempt` (0-basiert). `Retry-After` (Sekunden)
 * gewinnt vor dem Backoff, gekappt auf `generationRetry.maxDelayMs`.
 */
export function generationRetryDelayMs(attempt: number, retryAfterHeader?: string | null): number {
  const { backoffMs, maxDelayMs } = generationRetry;
  const fallback = backoffMs[Math.min(attempt, backoffMs.length - 1)] ?? maxDelayMs;
  const s = retryAfterHeader != null ? Number(retryAfterHeader) : NaN;
  const fromHeader = Number.isFinite(s) && s > 0 ? s * 1000 : NaN;
  return Math.min(Number.isFinite(fromHeader) ? fromHeader : fallback, maxDelayMs);
}
